import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "dropdown",
    "resultsList",
    "resultCount",
    "loadingIndicator",
    "selectedTemplate",
    "selectedName",
    "nameField",
    "assetTypeField",
    "statusField",
    "marketplaceTypeField",
    "brandField",
    "modelField",
    "descriptionField",
    "templateIdField",
    "warrantyDurationField"
  ]

  connect() {
    console.log("Asset template controller connected")
    this.searchTimeout = null
    this.currentPage = 1
    this.isLoading = false
    this.hasPerformedInitialSearch = false

    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener('click', this.boundClickOutside)

    // Add scroll listener for infinite scroll
    if (this.hasDropdownTarget) {
      this.dropdownTarget.addEventListener('scroll', this.handleScroll.bind(this))
    }
  }

  onFocus(event) {
    // When user clicks on the search input, show all templates if not already searched
    if (!this.hasPerformedInitialSearch && this.searchInputTarget.value.trim() === '') {
      this.currentPage = 1
      this.performSearch()
    } else if (this.resultsListTarget.children.length > 0) {
      // If we already have results, just show the dropdown
      this.showDropdown()
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  searchTemplates(event) {
    // Debounce search to avoid too many requests
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1
      this.performSearch()
    }, 300) // 300ms debounce
  }

  async performSearch() {
    const query = this.searchInputTarget.value.trim()

    // Allow empty search to show all templates (for browsing)
    // This makes it much easier to discover templates without knowing names
    
    this.showLoading()
    this.isLoading = true
    
    try {
      const searchUrl = this.searchInputTarget.dataset.searchUrl
      const url = `${searchUrl}?query=${encodeURIComponent(query)}&page=${this.currentPage}`
      const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': token
        }
      })
      
      if (!response.ok) {
        throw new Error('Search failed')
      }
      
      const data = await response.json()
      this.displayResults(data)
      
    } catch (error) {
      console.error('Error searching templates:', error)
      this.showError(t('javascript.controllers.asset_template_controller.search_failed'))
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  displayResults(data) {
    if (this.currentPage === 1) {
      // Clear existing results for new search
      this.resultsListTarget.innerHTML = ''
    }

    this.hasPerformedInitialSearch = true

    if (data.templates.length === 0 && this.currentPage === 1) {
      this.resultsListTarget.innerHTML = `
        <div class="no-results px-4 py-8 text-center text-[#8eb3cc]">
          ${t('javascript.controllers.asset_template_controller.no_templates_try_different')}
        </div>
      `
      this.resultCountTarget.textContent = t('javascript.controllers.asset_template_controller.no_templates_found')
    } else {
      // Add templates to the list
      data.templates.forEach(template => {
        const div = document.createElement('div')
        div.className = 'template-option px-4 py-3 hover:bg-[#1C2A38] cursor-pointer transition-colors border-b border-[#2f516a] last:border-b-0'
        div.dataset.templateId = template.id
        div.dataset.templateName = template.name
        div.dataset.templateType = template.asset_type
        div.dataset.action = 'click->asset-template#selectTemplate'

        div.innerHTML = `
          <div class="flex items-center gap-3">
            <span class="material-symbols-outlined text-primary !text-xl">description</span>
            <div class="flex-1">
              <div class="text-white font-medium">${this.escapeHtml(template.name)}</div>
              <div class="text-[#8eb3cc] text-sm">${this.escapeHtml(this.titleize(template.asset_type))}</div>
            </div>
          </div>
        `

        this.resultsListTarget.appendChild(div)
      })

      // Update count
      const countText = t('javascript.controllers.asset_template_controller.templates_found', { count: data.total_count })
      this.resultCountTarget.textContent = countText

      // Store if there are more results
      this.hasMore = data.has_more
    }

    this.showDropdown()
  }

  handleScroll(event) {
    // Implement infinite scroll
    const dropdown = event.target
    const scrolledToBottom = dropdown.scrollHeight - dropdown.scrollTop <= dropdown.clientHeight + 50
    
    if (scrolledToBottom && this.hasMore && !this.isLoading) {
      this.currentPage++
      this.performSearch()
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  showError(message) {
    this.resultsListTarget.innerHTML = `
      <div class="px-4 py-8 text-center text-red-400">
        <span class="material-symbols-outlined !text-2xl">error</span>
        <p class="mt-2">${this.escapeHtml(message)}</p>
      </div>
    `
    this.showDropdown()
  }

  showDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('hidden')
    }
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('hidden')
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  selectTemplate(event) {
    const option = event.currentTarget
    const templateId = option.dataset.templateId
    const templateName = option.dataset.templateName

    // Show loading state
    this.searchInputTarget.value = t('javascript.controllers.asset_template_controller.loading_template')
    this.searchInputTarget.disabled = true
    this.hideDropdown()

    // Set the template ID field to auto-link the asset to the template
    if (this.hasTemplateIdFieldTarget) {
      this.templateIdFieldTarget.value = templateId
    }

    // Fetch template data
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const url = this.searchInputTarget.dataset.templateUrl.replace(':template_id', templateId)

    console.log('Fetching template data from:', url)

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': token
      }
    })
      .then(response => {
        console.log('Response status:', response.status)
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.error || t('javascript.controllers.asset_template_controller.failed_to_load_template'))
          })
        }
        return response.json()
      })
      .then(data => {
        console.log('Template data received:', data)
        this.populateFields(data)
        this.showSuccess(templateName, templateId)
      })
      .catch(error => {
        console.error('Error loading template:', error)
        alert(t('javascript.controllers.asset_template_controller.failed_to_load_template_data', { error: error.message }))
        this.clearSelection()
      })
  }

  populateFields(data) {
    console.log('Populating fields with data:', data)

    try {
      // Populate basic fields with visual feedback
      if (this.hasNameFieldTarget && data.name) {
        this.nameFieldTarget.value = data.name
        this.flashField(this.nameFieldTarget)
      }

      if (this.hasAssetTypeFieldTarget && data.asset_type) {
        this.assetTypeFieldTarget.value = data.asset_type
        this.flashField(this.assetTypeFieldTarget)
      }

      if (this.hasStatusFieldTarget && data.status) {
        this.statusFieldTarget.value = data.status
        this.flashField(this.statusFieldTarget)
      }

      if (this.hasMarketplaceTypeFieldTarget && data.marketplace_type) {
        this.marketplaceTypeFieldTarget.value = data.marketplace_type
        this.flashField(this.marketplaceTypeFieldTarget)

        // Trigger marketplace type UI updates (unlock code section, owner visibility, warning)
        this.marketplaceTypeFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Populate unlock code fields from template
      this.populateUnlockCode(data)

      if (this.hasBrandFieldTarget && data.brand) {
        this.brandFieldTarget.value = data.brand || ''
        this.flashField(this.brandFieldTarget)
      }

      if (this.hasModelFieldTarget && data.model_number) {
        this.modelFieldTarget.value = data.model_number || ''
        this.flashField(this.modelFieldTarget)
      }

      // Populate warranty duration
      if (this.hasWarrantyDurationFieldTarget && data.warranty_duration_months) {
        this.warrantyDurationFieldTarget.value = data.warranty_duration_months
        this.flashField(this.warrantyDurationFieldTarget)
      }

      // Handle rich text description (Lexical editor)
      if (this.hasDescriptionFieldTarget && data.description) {
        const lexicalCtrl = this.application.getControllerForElementAndIdentifier(
          this.descriptionFieldTarget, "lexical-editor"
        )
        if (lexicalCtrl) {
          lexicalCtrl.loadHTML(data.description)
        } else {
          // Fallback for plain textarea
          this.descriptionFieldTarget.value = data.description
        }
        this.flashField(this.descriptionFieldTarget)
      }

      // Populate extra values (custom properties) with error handling
      if (data.extra_values && Object.keys(data.extra_values).length > 0) {
        try {
          console.log('Populating extra values:', data.extra_values)
          this.populateExtraValues(data.extra_values)
        } catch (error) {
          console.error('Error populating extra values:', error)
          // Don't fail the whole operation if extra values fail
        }
      }

      // Display template photos information
      if (data.photos && data.photos.length > 0) {
        try {
          console.log('Displaying template photos:', data.photos)
          this.displayTemplateAttachments('photos', data.photos)
        } catch (error) {
          console.error('Error displaying photos:', error)
        }
      }

      // Display template documents information
      if (data.documents && data.documents.length > 0) {
        try {
          console.log('Displaying template documents:', data.documents)
          this.displayTemplateAttachments('documents', data.documents)
        } catch (error) {
          console.error('Error displaying documents:', error)
        }
      }

      // Check maintenance policy checkboxes that match the template
      if (data.maintenance_policy_ids && data.maintenance_policy_ids.length > 0) {
        const policyCheckboxes = document.querySelectorAll('input[name="asset[maintenance_policy_ids][]"]')
        policyCheckboxes.forEach(checkbox => {
          if (data.maintenance_policy_ids.includes(parseInt(checkbox.value))) {
            checkbox.checked = true
            // Add visual feedback
            const label = checkbox.closest('label')
            if (label) {
              label.classList.add('ring-2', 'ring-green-500', 'rounded')
              setTimeout(() => {
                label.classList.remove('ring-2', 'ring-green-500')
              }, 1000)
            }
          }
        })
      }

      // Scroll to the form so user can see the populated fields
      setTimeout(() => {
        const formCard = document.querySelector('.bg-\\[\\#1C2A38\\].rounded-xl:last-of-type')
        if (formCard) {
          formCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }, 100)
    } catch (error) {
      console.error('Error in populateFields:', error)
      throw error // Re-throw to be caught by the caller
    }
  }

  populateExtraValues(extraValues) {
    console.log('populateExtraValues called with:', extraValues)

    // Find the extra-values container
    const extraValuesElement = document.querySelector('[data-controller="extra-values"]')
    if (!extraValuesElement) {
      console.warn('Extra values element not found')
      return
    }

    const container = extraValuesElement.querySelector('[data-extra-values-target="container"]')
    const emptyState = extraValuesElement.querySelector('[data-extra-values-target="emptyState"]')

    if (!container) {
      console.warn('Extra values container not found')
      return
    }

    // Clear existing extra values first
    container.innerHTML = ''

    // Try to get the Stimulus controller
    let controller = null
    try {
      controller = this.application.getControllerForElementAndIdentifier(extraValuesElement, 'extra-values')
    } catch (error) {
      console.warn('Could not get extra-values controller:', error)
    }

    // Add each key-value pair from the template
    const addButton = extraValuesElement.querySelector('[data-action*="extra-values#add"]')
    if (!addButton) {
      console.error('Add button not found')
      return
    }

    Object.entries(extraValues).forEach(([key, value], index) => {
      console.log(`Adding extra value ${index + 1}:`, key, '=', value)

      setTimeout(() => {
        // Click the add button
        addButton.click()

        // Wait for DOM to update
        setTimeout(() => {
          const pairs = container.querySelectorAll('.extra-value-pair')
          const lastPair = pairs[pairs.length - 1]

          if (lastPair) {
            const keyInput = lastPair.querySelector('[data-field="key"]')
            const valueInput = lastPair.querySelector('[data-field="value"]')

            if (keyInput && valueInput) {
              keyInput.value = key
              keyInput.dispatchEvent(new Event('input', { bubbles: true }))

              valueInput.value = value
              valueInput.dispatchEvent(new Event('input', { bubbles: true }))

              console.log(`✓ Added: ${key} = ${value}`)

              lastPair.classList.add('ring-2', 'ring-green-500')
              setTimeout(() => lastPair.classList.remove('ring-2', 'ring-green-500'), 1000)
            }
          }
        }, 50)
      }, index * 100)
    })

    // Update counter after all pairs added
    const totalPairs = Object.keys(extraValues).length
    setTimeout(() => {
      if (controller && controller.updateCounter) {
        controller.updateCounter()
        controller.updateEmptyState()
      }
      if (emptyState) {
        emptyState.classList.add('hidden')
      }
      console.log('✅ Extra values done')
    }, totalPairs * 100 + 150)
  }

  populateUnlockCode(data) {
    const isMarketplace = data.marketplace_type === 'b2c' || data.marketplace_type === 'b2b'
    const unlockSection = document.getElementById('asset-unlock-code-section')
    const modeSelect = document.getElementById('asset_unlock_code_mode')
    const valueField = document.getElementById('asset-unlock-value-field')

    if (!unlockSection || !modeSelect) return

    // Show/hide the unlock code section based on marketplace type
    unlockSection.classList.toggle('hidden', !isMarketplace)

    if (!isMarketplace) return

    // Map template modes to asset modes: "constant" -> "custom", "random" -> "random", "none" -> "none"
    const templateMode = data.unlock_code_mode || 'none'
    let assetMode = 'none'
    if (templateMode === 'constant') {
      assetMode = 'custom'
    } else if (templateMode === 'random') {
      assetMode = 'random'
    }

    modeSelect.value = assetMode
    this.flashField(modeSelect)

    // Show/hide the custom value field and populate it
    if (valueField) {
      valueField.classList.toggle('hidden', assetMode !== 'custom')

      if (assetMode === 'custom' && data.unlock_code_value) {
        const input = valueField.querySelector('input[name="asset[unlock_code]"]')
        if (input) {
          input.value = data.unlock_code_value
          this.flashField(input)
        }
      }
    }

    console.log(`Unlock code set: mode=${assetMode}, from template mode=${templateMode}`)
  }

  displayTemplateAttachments(type, attachments) {
    console.log(`Displaying template ${type}:`, attachments)

    // Find the appropriate section (photos or documents)
    // English section titles are only used to locate the section in the DOM (never displayed);
    // the icon-ligature fallback below ('image' / 'description') is locale-independent.
    const sectionTitle = type === 'photos' ? 'Photos' : 'Documents & Files'

    // Try multiple approaches to find the section
    let section = null

    // Approach 1: Find h2 with exact text match
    const headings = Array.from(document.querySelectorAll('h2'))
    const matchingHeading = headings.find(h2 =>
      h2.textContent.trim().includes(sectionTitle) ||
      h2.textContent.trim().includes(type === 'photos' ? 'image' : 'description')
    )

    if (matchingHeading) {
      // Find the parent container - look for the mb-8 div that contains this section
      section = matchingHeading.closest('.mb-8')
      console.log(`Found section via heading for ${sectionTitle}:`, section)
    }

    // Approach 2: If not found, try finding by the bg-dark-card container
    if (!section) {
      const cards = Array.from(document.querySelectorAll('.bg-dark-card'))
      section = cards.find(card => {
        const heading = card.querySelector('h2')
        return heading && heading.textContent.includes(sectionTitle)
      })
      if (section) {
        section = section.closest('.mb-8')
        console.log(`Found section via card for ${sectionTitle}:`, section)
      }
    }

    if (!section) {
      console.error(`❌ Section not found for ${sectionTitle}`)
      console.log('Available h2 elements:', headings.map(h => h.textContent.trim()))
      return
    }

    // Create or update info banner
    let infoBanner = section.querySelector('.template-attachments-info')
    if (!infoBanner) {
      infoBanner = document.createElement('div')
      infoBanner.className = 'template-attachments-info mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg'

      // Try to insert before the bg-dark-card
      const card = section.querySelector('.bg-dark-card')
      if (card) {
        section.insertBefore(infoBanner, card)
        console.log('Inserted banner before card')
      } else {
        // Try to insert after the h2
        const h2 = section.querySelector('h2')
        if (h2 && h2.parentNode === section) {
          h2.insertAdjacentElement('afterend', infoBanner)
          console.log('Inserted banner after h2')
        } else {
          // Fallback: prepend to section
          section.insertBefore(infoBanner, section.firstChild)
          console.log('Inserted banner at section start')
        }
      }
    }

    const filesList = attachments.map(file => `
      <li class="flex items-center gap-2">
        <span class="material-symbols-outlined !text-sm text-green-400">check_circle</span>
        <span class="text-sm text-green-200">${this.escapeHtml(file.filename)}</span>
        <span class="text-xs text-green-300/70">(${this.formatFileSize(file.byte_size)})</span>
      </li>
    `).join('')

    infoBanner.innerHTML = `
      <div class="flex items-start gap-3">
        <span class="material-symbols-outlined text-green-400 !text-xl">cloud_download</span>
        <div class="flex-1">
          <p class="text-green-200 font-medium mb-2">
            ${type === 'photos'
              ? t('javascript.controllers.asset_template_controller.photos_will_be_copied', { count: attachments.length })
              : t('javascript.controllers.asset_template_controller.documents_will_be_copied', { count: attachments.length })}
          </p>
          <ul class="space-y-1 ml-2">
            ${filesList}
          </ul>
          <p class="text-green-300/70 text-sm mt-2">
            ${type === 'photos' ? t('javascript.controllers.asset_template_controller.files_auto_copied_photos') : t('javascript.controllers.asset_template_controller.files_auto_copied_documents')}
          </p>
        </div>
      </div>
    `

    // Flash the banner
    infoBanner.classList.add('ring-2', 'ring-green-500')
    setTimeout(() => {
      infoBanner.classList.remove('ring-2', 'ring-green-500')
    }, 1500)

    console.log(`${type} info banner displayed`)
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  flashField(field) {
    // Add visual feedback when a field is populated
    field.classList.add('ring-2', 'ring-green-500')
    setTimeout(() => {
      field.classList.remove('ring-2', 'ring-green-500')
    }, 1000)
  }

  showSuccess(templateName, templateId) {
    // Show selected template indicator
    this.searchInputTarget.value = ''
    this.searchInputTarget.disabled = false
    this.searchInputTarget.placeholder = t('javascript.controllers.asset_template_controller.search_another_template')

    this.selectedNameTarget.textContent = templateName
    this.selectedTemplateTarget.classList.remove('hidden')

    // Store the template ID for clearing later
    this.selectedTemplateTarget.dataset.templateId = templateId

    // Hide dropdown
    this.hideDropdown()
  }

  clearSelection(event) {
    if (event) event.preventDefault()

    // Clear the template ID field
    if (this.hasTemplateIdFieldTarget) {
      this.templateIdFieldTarget.value = ''
    }

    // Clear the search
    this.searchInputTarget.value = ''
    this.searchInputTarget.disabled = false
    this.searchInputTarget.placeholder = t('javascript.controllers.asset_template_controller.click_to_browse_or_search')

    // Hide selected indicator
    this.selectedTemplateTarget.classList.add('hidden')
    delete this.selectedTemplateTarget.dataset.templateId

    // Clear results
    this.resultsListTarget.innerHTML = ''
    this.resultCountTarget.textContent = t('javascript.controllers.asset_template_controller.click_to_browse_all_templates')
    this.hasPerformedInitialSearch = false

    // Clear all form fields
    this.clearFields()

    // Hide dropdown
    this.hideDropdown()
  }

  clearFields() {
    // Clear all form fields
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = ''
    }

    if (this.hasAssetTypeFieldTarget) {
      this.assetTypeFieldTarget.selectedIndex = 0
    }

    if (this.hasStatusFieldTarget) {
      this.statusFieldTarget.selectedIndex = 0
    }

    if (this.hasBrandFieldTarget) {
      this.brandFieldTarget.value = ''
    }

    if (this.hasModelFieldTarget) {
      this.modelFieldTarget.value = ''
    }

    if (this.hasWarrantyDurationFieldTarget) {
      this.warrantyDurationFieldTarget.value = ''
    }

    if (this.hasDescriptionFieldTarget) {
      const lexicalCtrl = this.application.getControllerForElementAndIdentifier(
        this.descriptionFieldTarget, "lexical-editor"
      )
      if (lexicalCtrl) {
        lexicalCtrl.loadHTML("")
      } else {
        this.descriptionFieldTarget.value = ""
      }
    }

    // Clear extra values
    const extraValuesElement = document.querySelector('[data-controller="extra-values"]')
    if (extraValuesElement) {
      const container = extraValuesElement.querySelector('[data-extra-values-target="container"]')
      if (container) {
        container.innerHTML = ''
      }
      const emptyState = extraValuesElement.querySelector('[data-extra-values-target="emptyState"]')
      if (emptyState) {
        emptyState.classList.remove('hidden')
      }
    }

    // Reset unlock code section
    const unlockSection = document.getElementById('asset-unlock-code-section')
    const unlockModeSelect = document.getElementById('asset_unlock_code_mode')
    const unlockValueField = document.getElementById('asset-unlock-value-field')
    if (unlockSection) unlockSection.classList.add('hidden')
    if (unlockModeSelect) unlockModeSelect.value = 'none'
    if (unlockValueField) {
      unlockValueField.classList.add('hidden')
      const input = unlockValueField.querySelector('input[name="asset[unlock_code]"]')
      if (input) input.value = ''
    }

    // Reset marketplace type
    if (this.hasMarketplaceTypeFieldTarget) {
      this.marketplaceTypeFieldTarget.value = 'internal'
      this.marketplaceTypeFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Remove template attachment info banners
    document.querySelectorAll('.template-attachments-info').forEach(banner => {
      banner.remove()
    })

    // Uncheck all maintenance policy checkboxes
    const policyCheckboxes = document.querySelectorAll('input[name="asset[maintenance_policy_ids][]"]')
    policyCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })
  }

  // Helper methods
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  titleize(str) {
    return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
}
